// React-Query hooks для survey (Sprint 2 A-3, A-4, A-5).
// Source of truth: docs/specs/sprint-3/stage-1-contracts.md §8.1.
//
// PATCH /survey/step/{n} body — обёрнут в { step: {...} } (см. stage-4 §5.10 / UpdateSurveyStepRequest.php).
// Hook принимает PatchSurveyStepRequest<N> = { step: SurveyStep{N}Request } — request-шейп
// с optional `sometimes nullable` полями, не persisted Survey{N}<Basic|...>. См. types.ts.

import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationOptions, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type {
  ApiError,
  GetSurveyResponse,
  PatchSurveyStepRequest,
  PatchSurveyStepResponse,
  ProfileMeResponse,
  PutSurveyRequest,
  PutSurveyResponse,
  Survey,
} from '@/api/types';

// Sprint 5A.1 Sayat R4 race fix: после PATCH/PUT survey бэк возвращает свежую
// `Survey` (с completed=true когда все 5 шагов валидны). Если просто invalidate'нуть
// profileMe — refetch async, OnboardingRouter читает СТАРЫЕ данные (completed=false)
// → derives 'surveyPending' → redirect назад на /survey/step-N → user видит «два клика».
// Fix: optimistically обновляем profileMe cache новой survey ДО navigation, чтобы
// OnboardingRouter сразу видел completed=true.
function syncProfileMeSurveyCache(qc: QueryClient, survey: Survey): void {
  const current = qc.getQueryData<ProfileMeResponse>(queryKeys.profileMe);
  if (current === undefined) return;
  qc.setQueryData<ProfileMeResponse>(queryKeys.profileMe, {
    ...current,
    survey: { exists: true, ...survey },
  });
}

export function useSurvey(enabled = true): UseQueryResult<GetSurveyResponse, AxiosError<ApiError>> {
  return useQuery({
    queryKey: queryKeys.survey,
    queryFn: async () => {
      const { data } = await api.get<GetSurveyResponse>('/survey');
      return data;
    },
    enabled,
  });
}

// R1 M3 (Sprint 5A.2) — extract options в pure factory для T-S5A2-15 testability через
// QueryClient mock (mirror uploads.ts:129-148 makeConfirmUploadOptions pattern). Hooks
// остаются thin wrappers (public API сохраняется byte-for-byte).
export function makePutSurveyOptions(
  qc: QueryClient,
): UseMutationOptions<PutSurveyResponse, AxiosError<ApiError>, PutSurveyRequest> {
  return {
    mutationFn: async (body) => {
      const { data } = await api.put<PutSurveyResponse>('/survey', body);
      return data;
    },
    onSuccess: async (data) => {
      // Sayat R4 race fix: optimistic update ДО invalidate.
      syncProfileMeSurveyCache(qc, data.survey);

      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.profileMe }),
        qc.invalidateQueries({ queryKey: queryKeys.survey }),
      ]);

      // Sprint 5A.1 + 5A.2 (R4 H1) — conditional cache invalidation.
      // wellness_cache_invalidated=true означает «survey payload changed»:
      //   - wellness: точная dependency (Stage 1 5A.1 §4.5)
      //   - biomarker insights: conservative superset (only derived patient_context
      //     участвует в biomarker_set_hash; backend cache-hit если не изменился).
      if (data.wellness_cache_invalidated) {
        await Promise.all([
          qc.invalidateQueries({ queryKey: queryKeys.wellnessInsights }),
          qc.invalidateQueries({ queryKey: queryKeys.biomarkerInsights }),
        ]);
      }
    },
  };
}

export function usePutSurvey(): UseMutationResult<PutSurveyResponse, AxiosError<ApiError>, PutSurveyRequest> {
  const qc = useQueryClient();
  return useMutation(makePutSurveyOptions(qc));
}

export function makePatchSurveyStepOptions<N extends 1 | 2 | 3 | 4 | 5>(
  qc: QueryClient,
  n: N,
): UseMutationOptions<PatchSurveyStepResponse, AxiosError<ApiError>, PatchSurveyStepRequest<N>> {
  return {
    mutationFn: async (body) => {
      const { data } = await api.patch<PatchSurveyStepResponse>(`/survey/step/${n}`, body);
      return data;
    },
    onSuccess: async (data) => {
      syncProfileMeSurveyCache(qc, data.survey);
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.profileMe }),
        qc.invalidateQueries({ queryKey: queryKeys.survey }),
      ]);
      if (data.wellness_cache_invalidated) {
        await Promise.all([
          qc.invalidateQueries({ queryKey: queryKeys.wellnessInsights }),
          qc.invalidateQueries({ queryKey: queryKeys.biomarkerInsights }),
        ]);
      }
    },
  };
}

export function usePatchSurveyStep<N extends 1 | 2 | 3 | 4 | 5>(
  n: N,
): UseMutationResult<PatchSurveyStepResponse, AxiosError<ApiError>, PatchSurveyStepRequest<N>> {
  const qc = useQueryClient();
  return useMutation(makePatchSurveyStepOptions(qc, n));
}
